import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import ArrowRightIcon from "@redotech/redo-icons/arrow-right.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoIcon } from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useEffect, useRef, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";

export type CopyLatestFlowType = "return" | "claim" | "warranty";

interface TeamOption {
  _id: string;
  name: string;
}

const flowTypeLabel: Record<CopyLatestFlowType, string> = {
  return: "return",
  claim: "claim",
  warranty: "warranty",
};

const flowTypeButtonLabel: Record<CopyLatestFlowType, string> = {
  return: "Return",
  claim: "Claim",
  warranty: "Warranty",
};

export const CopyLatestFlowSettings = memo(function CopyLatestFlowSettings({
  sourceTeamId,
  sourceTeamName,
  flowType,
}: {
  sourceTeamId: string;
  sourceTeamName: string;
  flowType: CopyLatestFlowType;
}) {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const [destinationTeamId, setDestinationTeamId] = useState("");
  const [destinationTeamName, setDestinationTeamName] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [teamOptions, setTeamOptions] = useState<TeamOption[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout>();
  const isSelectingRef = useRef(false);

  const label = flowTypeLabel[flowType];
  const buttonLabel = flowTypeButtonLabel[flowType];

  const searchTeams = useHandler(async (query: string) => {
    if (query.trim().length < 2) {
      setTeamOptions([]);
      setShowDropdown(false);
      return;
    }

    setIsSearching(true);
    try {
      const result = await rpcClient.searchTeams({
        query: query.trim(),
        limit: 10,
      });
      setTeamOptions(result.teams);
      setShowDropdown(true);
    } catch (error) {
      console.error("Team search failed:", error);
      setTeamOptions([]);
      setShowDropdown(false);
    } finally {
      setIsSearching(false);
    }
  });

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (isSelectingRef.current) {
      return;
    }

    if (searchQuery.trim().length >= 2) {
      searchTimeoutRef.current = setTimeout(async () => {
        await searchTeams(searchQuery);
      }, 300);
    } else {
      setTeamOptions([]);
      setShowDropdown(false);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, searchTeams]);

  const handleTeamSelect = useHandler((team: TeamOption) => {
    isSelectingRef.current = true;
    setDestinationTeamId(team._id);
    setDestinationTeamName(team.name);
    setSearchQuery(`${team.name} (${team._id})`);
    setShowDropdown(false);

    requestAnimationFrame(() => {
      isSelectingRef.current = false;
    });
  });

  const handleInputChange = useHandler((value: string) => {
    setSearchQuery(value);

    const parenthesesMatch = value.match(/\(([^)]+)\)/);

    if (parenthesesMatch && parenthesesMatch[1]) {
      setDestinationTeamId(parenthesesMatch[1].trim());
      const namePart = value.split("(")[0]?.trim() ?? "";
      setDestinationTeamName(namePart);
    } else if (value.trim()) {
      setDestinationTeamId(value.trim());
      setDestinationTeamName("");
    } else {
      setDestinationTeamId("");
      setDestinationTeamName("");
    }
  });

  const handleOpenConfirm = useHandler(() => {
    setMessage(null);
    setConfirmOpen(true);
  });

  const handleCloseConfirm = useHandler(() => {
    if (isLoading) {
      return;
    }
    setConfirmOpen(false);
  });

  const handleCopy = useHandler(async () => {
    setIsLoading(true);

    try {
      const result = await rpcClient.copyLatestFlow({
        sourceTeamId,
        destinationTeamId: destinationTeamId.trim(),
        flowType,
      });

      setMessage({
        type: result.success ? "success" : "error",
        text: result.message || "Operation completed",
      });

      if (result.success) {
        setDestinationTeamId("");
        setDestinationTeamName("");
        setSearchQuery("");
        setShowDropdown(false);
      }
    } catch (error) {
      setMessage({
        type: "error",
        text: `Failed to copy ${label} flow: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      });
    } finally {
      setIsLoading(false);
      setConfirmOpen(false);
    }
  });

  const isFormValid = destinationTeamId.trim() !== "";

  return (
    <Flex dir="column">
      <Text fontSize="sm" textColor="secondary">
        Copy and publish the most recently published {label} flow from this team
        to another team
      </Text>

      <div style={{ position: "relative" }}>
        <RedoTextInput
          label="Destination Team"
          placeholder="Search by team name or enter team ID..."
          setValue={handleInputChange}
          value={searchQuery}
        />
        {isSearching && (
          <Text fontSize="xs" textColor="secondary">
            Searching teams...
          </Text>
        )}
        {showDropdown && teamOptions.length > 0 && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              backgroundColor: "var(--redo-colors-background-bg-primary)",
              border: "1px solid #e1e5e9",
              borderRadius: "var(--border-radius-sm)",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              zIndex: 1000,
              maxHeight: "200px",
              overflowY: "auto",
            }}
          >
            {teamOptions.map((team) => (
              <div
                key={team._id}
                onClick={() => handleTeamSelect(team)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#f8f9fa";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "white";
                }}
                style={{
                  padding: "var(--redo-spacing-md) var(--redo-spacing-lg)",
                  cursor: "pointer",
                  borderBottom: "1px solid #f5f5f5",
                }}
              >
                <Text fontSize="sm" fontWeight="medium">
                  {team.name}
                </Text>
                <Text fontSize="xs" textColor="secondary">
                  {team._id}
                </Text>
              </div>
            ))}
          </div>
        )}
      </div>
      {searchQuery.trim() !== "" &&
        !showDropdown &&
        teamOptions.length === 0 &&
        !isSearching && (
          <Text fontSize="xs" textColor="secondary">
            No teams found matching your search.
          </Text>
        )}

      <Flex>
        <RedoButton
          disabled={!isFormValid || isLoading}
          hierarchy="primary"
          onClick={handleOpenConfirm}
          text={`Copy and Publish ${buttonLabel} Flow`}
        />
      </Flex>

      {message && (
        <Text
          fontSize="sm"
          textColor={message.type === "success" ? "success" : "error"}
        >
          {message.text}
        </Text>
      )}

      <CopyFlowConfirmModal
        destinationTeamId={destinationTeamId}
        destinationTeamName={destinationTeamName}
        flowType={flowType}
        isLoading={isLoading}
        onClose={handleCloseConfirm}
        onConfirm={handleCopy}
        open={confirmOpen}
        sourceTeamName={sourceTeamName}
      />
    </Flex>
  );
});

const CopyFlowConfirmModal = memo(function CopyFlowConfirmModal({
  open,
  onClose,
  onConfirm,
  isLoading,
  flowType,
  sourceTeamName,
  destinationTeamName,
  destinationTeamId,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  flowType: CopyLatestFlowType;
  sourceTeamName: string;
  destinationTeamName: string;
  destinationTeamId: string;
}) {
  const label = flowTypeLabel[flowType];

  return (
    <RedoModal
      isOpen={open}
      modalSize="sm"
      onModalCloseRequested={onClose}
      primaryButton={{
        text: "Copy and Publish",
        onClick: onConfirm,
        disabled: isLoading,
        pending: isLoading,
      }}
      secondaryButton={{ text: "Cancel", onClick: onClose }}
      theme="warn"
      title={`Copy ${label} flow?`}
    >
      <Flex dir="column" gap="lg" p="lg">
        <Flex align="center" dir="row" gap="md" justify="center">
          <Flex align="center" dir="column" gap="xxs">
            <Text fontSize="xs" textColor="secondary">
              From
            </Text>
            <Text fontSize="sm" fontWeight="medium">
              {sourceTeamName}
            </Text>
          </Flex>
          <RedoIcon color="tertiary" Icon={ArrowRightIcon} size={20} />
          <Flex align="center" dir="column" gap="xxs">
            <Text fontSize="xs" textColor="secondary">
              To
            </Text>
            <Text fontSize="sm" fontWeight="medium">
              {destinationTeamName || destinationTeamId}
            </Text>
          </Flex>
        </Flex>
        <Text fontSize="sm" textAlign="center" textColor="error">
          Warning: This will overwrite the current {label} flow on the
          destination team. The existing flow cannot be recovered.
        </Text>
      </Flex>
    </RedoModal>
  );
});
